import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { DragDropModule } from '@angular/cdk/drag-drop';
import type { OnInit } from '@angular/core';
import { Component, effect, inject, Injector, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { OnyxFormMode } from '../../../../enums';
import { FormHelper } from '../../../../helpers/form.helper';
import { I18N_NAMESPACE } from '../../../../internal/constants';
import type { OnyxUserSettingDto } from '../../../../services/interfaces';
import { OnyxToastService } from '../../../../services/onyx-toast.service';
import { OnyxUserSettingsService } from '../../../../services/onyx-user-settings.service';
import { onyxTextFieldLengthValidator } from '../../../../validators/onyx-text-field-length.validator';
import { OnyxButtonComponent } from '../../../buttons/onyx-button/onyx-button.component';
import { OnyxDropdownComponent } from '../../../dropdown/onyx-dropdown/onyx-dropdown.component';
import { OnyxIconPickerComponent } from '../../../icon-picker/onyx-icon-picker/onyx-icon-picker.component';
import { OnyxModalComponent } from '../../../modal/onyx-modal/onyx-modal.component';
import { OnyxTextFieldComponent } from '../../../text-field/onyx-text-field/onyx-text-field.component';
import { OnyxToastMessage } from '../../../toast/enums';
import { ONYX_TABLE_VIEW_DEFAULT_ICON } from '../../constants';
import type {
  OnyxTableColumn,
  OnyxTableView,
  OnyxTableViewValue,
} from '../../interfaces';
import { OnyxTableColumnsPickerComponent } from '../onyx-table-columns-picker/onyx-table-columns-picker.component';

export type OnyxTableViewModalData = {
  scope: string;
} & OnyxTableViewModalVariant &
  (
    | {
        mode: OnyxFormMode.ADD;
        value: Pick<OnyxTableViewValue, 'columns' | 'filters' | 'sort'>;
      }
    | ({
        mode: OnyxFormMode.EDIT;
      } & OnyxTableView)
  );

export type OnyxTableViewModalResult = OnyxTableView | 'delete';

type OnyxTableViewModalVariant =
  | {
      variant: 'table';
      columns: OnyxTableColumn<any>[];
      defaultView: OnyxTableView;
    }
  | { variant: 'dropdown'; requiredColumns: string[] };

@Component({
  selector: 'onyx-table-view-modal',
  imports: [
    OnyxModalComponent,
    TranslatePipe,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxButtonComponent,
    DragDropModule,
    OnyxIconPickerComponent,
    OnyxDropdownComponent,
    OnyxTableColumnsPickerComponent,
  ],
  templateUrl: './onyx-table-view-modal.component.html',
  styleUrl: './onyx-table-view-modal.component.scss',
})
export class OnyxTableViewModalComponent implements OnInit {
  protected readonly data = inject<OnyxTableViewModalData>(DIALOG_DATA);
  protected readonly dialogRef = inject(DialogRef<OnyxTableViewModalResult>);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly toastService = inject(OnyxToastService);
  private readonly userSettingsService = inject(OnyxUserSettingsService);
  private readonly injector = inject(Injector);

  protected readonly I18N = `${I18N_NAMESPACE}.table.viewModal`;
  protected readonly I18N_COMMON = `${I18N_NAMESPACE}.common`;

  protected readonly OnyxFormMode = OnyxFormMode;

  protected form = this.buildForm(this.data.variant);
  protected value = signal<string[]>(this.data.value.columns);
  protected loading = signal<'delete' | 'submit' | null>(null);
  protected close$ = new Subject<OnyxTableViewModalResult | null>();

  public ngOnInit(): void {
    if (this.data.mode === OnyxFormMode.EDIT) {
      this.form.patchValue({
        name: this.data.name,
        icon: this.data.value.icon,
      });
    }

    if (this.data.variant !== 'table') return;

    effect(() => this.form?.controls?.value?.setValue(this.value()), {
      injector: this.injector,
    });
  }

  protected delete(): void {
    if (this.data.mode !== OnyxFormMode.EDIT) return;

    this.loading.set('delete');
    this.userSettingsService
      .deleteSetting(this.data.scope, this.data.uuid)
      .subscribe(() => {
        this.toastService.showSuccess({
          message: `${this.I18N}.deleteSuccess`,
        });

        this.close$.next('delete');
      })
      .add(() => this.loading.set(null));
  }

  protected cancel(): void {
    this.close$.next(null);
  }

  protected submit(): void {
    if (this.form.invalid) {
      FormHelper.submit(this.form);
      this.toastService.showCustom(OnyxToastMessage.INVALID_DATA);
      return;
    }

    const form = this.form.getRawValue();
    const { variant, mode, value } = this.data;
    const dto: OnyxUserSettingDto = {
      name: form.name,
      value: JSON.stringify({
        icon: form.icon,
        columns:
          variant === 'table'
            ? form.value
            : mode === OnyxFormMode.EDIT
              ? value.columns
              : this.data.requiredColumns,
        ...(variant === 'table' && { filters: value.filters }),
        sort: value.sort,
        ...(variant === 'dropdown' &&
          mode === OnyxFormMode.EDIT && {
            isDefault: value.isDefault,
          }),
      }),
    };

    const { scope } = this.data;
    const action$ =
      this.data.mode === OnyxFormMode.ADD
        ? this.userSettingsService.addSetting(scope, dto)
        : this.userSettingsService.editSetting(scope, this.data.uuid, dto);

    this.loading.set('submit');
    action$
      .subscribe((setting) => {
        this.toastService.showSuccess({
          message: `${this.I18N}.${this.data.mode}Success`,
        });
        this.close$.next({
          uuid: setting.uuid,
          name: setting.name,
          value: JSON.parse(setting.value),
        });
      })
      .add(() => this.loading.set(null));
  }

  private buildForm(variant: OnyxTableViewModalVariant['variant']) {
    return this.fb.group({
      name: this.fb.control('', [
        Validators.required,
        onyxTextFieldLengthValidator,
      ]),
      icon: this.fb.control(ONYX_TABLE_VIEW_DEFAULT_ICON, [
        Validators.required,
      ]),
      ...(variant === 'table' && {
        value: this.fb.control<string[]>([], [Validators.required]),
      }),
    });
  }
}
